import { QueryClient } from '@tanstack/react-query';
import { HttpError, ValidationError } from '@personal-portfolio/api-client';

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

// Retry transient failures only: contract violations and 4xx answers are
// deterministic — retrying them just burns requests. Exported so the truth
// table is unit-testable without spinning up a QueryClient.
export const shouldRetryQuery = (
  failureCount: number,
  error: unknown,
): boolean =>
  // Budget of 3 attempts total. The CMS is a single container behind nginx, so
  // a real outage is not something extra attempts will ride out; more retries
  // would only delay the error state the user is going to see anyway.
  failureCount < 2 &&
  !(error instanceof ValidationError) &&
  !(error instanceof HttpError && error.status < 500);

// 24h staleTime in prod is safe because the content is a personal portfolio
// edited by hand a few times a year: a visitor seeing yesterday's copy costs
// nothing, while refetching on every mount would hit the CMS constantly. Dev
// drops to a minute so CMS edits show up without clearing the cache.
export const createPortfolioQueryClient = ({
  isDev,
}: {
  isDev: boolean;
}): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: isDev ? 60_000 : DAY_MS,
        // Must be >= the persister's maxAge (7 days, see query-provider) or
        // restored entries are garbage-collected before they can render.
        gcTime: WEEK_MS,
        // Default 'online' refuses to run the queryFn at all while offline:
        // with a cold cache that means an infinite pending state before a
        // single request is even attempted. 'offlineFirst' always runs it
        // once, so the fetch fails fast with a NetworkError and there is a
        // real error to show; warm-cache pages render persisted data while
        // the background refetch fails silently.
        //
        // It does NOT keep the RETRIES running. query-core pauses a retryer
        // whenever the window loses focus or the browser goes offline
        // (`focusManager.isFocused() && onlineManager.isOnline()` in
        // retryer.ts — only networkMode 'always' skips the online half, and
        // nothing skips the focus half). A paused query sits at status
        // 'pending' indefinitely, so consumers must not treat `isPending`
        // alone as "still loading": see `isPaused` in site-content-gate and
        // in toErrorMessage, which is what stops the splash and the pages
        // spinning forever.
        networkMode: 'offlineFirst',
        retry: shouldRetryQuery,
        refetchOnWindowFocus: false,
      },
    },
  });
